/**
 * QueryBuilder: fluent API for queries.
 */

import type { EntityManager } from './entity-manager';
import type { WhereClause, OrderByClause } from './types';

export class QueryBuilder<T extends Record<string, unknown>> {
  private _manager: EntityManager<T>;
  private _whereClause: WhereClause = {};
  private _orderByClause: OrderByClause = {};
  private _limitValue?: number;
  private _offsetValue?: number;
  private _selectColumns?: string[];
  private _relations: string[] = [];

  constructor(manager: EntityManager<T>) {
    this._manager = manager;
  }

  select(...columns: (keyof T | string)[]): this {
    this._selectColumns = columns as string[];
    return this;
  }

  where(clause: WhereClause): this {
    if (typeof clause === 'string') {
      this._whereClause = clause;
    } else {
      this._whereClause = { ...(this._whereClause as Record<string, unknown>), ...clause };
    }
    return this;
  }

  andWhere(clause: WhereClause): this {
    return this.where(clause);
  }

  orderBy(column: keyof T | string, direction: 'ASC' | 'DESC' = 'ASC'): this {
    (this._orderByClause as Record<string, 'ASC' | 'DESC'>)[column as string] = direction;
    return this;
  }

  limit(value: number): this {
    this._limitValue = value;
    return this;
  }

  offset(value: number): this {
    this._offsetValue = value;
    return this;
  }

  skip(value: number): this {
    return this.offset(value);
  }

  take(value: number): this {
    return this.limit(value);
  }

  withRelation(...relations: string[]): this {
    this._relations.push(...relations);
    return this;
  }

  async getMany(): Promise<T[]> {
    return this._manager.find({
      where: this._whereClause,
      orderBy: Object.keys(this._orderByClause).length > 0 ? this._orderByClause : undefined,
      limit: this._limitValue,
      offset: this._offsetValue,
      select: this._selectColumns,
      relations: this._relations,
    });
  }

  async getOne(): Promise<T | null> {
    const results = await this._manager.find({
      where: this._whereClause,
      orderBy: Object.keys(this._orderByClause).length > 0 ? this._orderByClause : undefined,
      limit: 1,
      offset: this._offsetValue,
      select: this._selectColumns,
      relations: this._relations,
    });
    return results[0] ?? null;
  }

  async getCount(): Promise<number> {
    return this._manager.count(this._whereClause);
  }

  async exists(): Promise<boolean> {
    return this._manager.exists(this._whereClause);
  }

  async delete(): Promise<number> {
    return this._manager.delete(this._whereClause);
  }

  async update(data: Partial<T>): Promise<number> {
    return this._manager.update(data, this._whereClause);
  }
}
